import type { PropertyDetailListing } from '@/src/components/features/property/types';

const DEFAULT_HDP_BACK_HREF = '/';
const DEFAULT_SRP_SLUG = 'apartments-for-rent';

type AreaLike = {
  resourceId?: string | null;
  type?: string | null;
  uriV2?: string | null;
};

type ListingBackHrefSource = Partial<Pick<PropertyDetailListing, 'address'>> & {
  areas?: {
    breadcrumbs?: Array<AreaLike> | null;
    specific?: AreaLike | null;
  } | null;
};

function trimSlashes(value: string) {
  return value.replace(/^\/+|\/+$/g, '');
}

function srpHrefFromResourceId(resourceId: string | null | undefined): string | null {
  const normalized = resourceId?.trim();
  if (!normalized) return null;
  return `/${trimSlashes(normalized)}/${DEFAULT_SRP_SLUG}`;
}

function srpHrefFromArea(area: AreaLike | null | undefined): string | null {
  const hrefFromResourceId = srpHrefFromResourceId(area?.resourceId);
  if (hrefFromResourceId) return hrefFromResourceId;

  const uri = area?.uriV2?.trim();
  if (!uri?.startsWith('/')) return null;
  return uri.includes(`/${DEFAULT_SRP_SLUG}`) ? uri : `${uri.replace(/\/$/g, '')}/${DEFAULT_SRP_SLUG}`;
}

function findCityArea(areas: ListingBackHrefSource['areas']): AreaLike | null {
  const cityBreadcrumb = areas?.breadcrumbs?.find((breadcrumb) => breadcrumb?.type?.toLowerCase() === 'city');
  if (cityBreadcrumb) return cityBreadcrumb;

  return areas?.specific?.type?.toLowerCase() === 'city' ? areas.specific : null;
}

function slugifyResourcePart(value: string | null | undefined): string | null {
  const slug = value
    ?.trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  return slug || null;
}

function cityResourceIdFromAddress(address: ListingBackHrefSource['address']): string | null {
  const city = slugifyResourcePart(address?.city);
  const state = slugifyResourcePart(address?.state);
  return city && state ? `${city}-${state}` : null;
}

/**
 * HDP back links need a deterministic destination because direct SSR visits
 * and shared listing URLs have no SRP entry in browser history. Prefer the
 * city breadcrumb so neighborhood/building listings still return to the
 * broader city SRP.
 */
export function getListingBackHref(listing: ListingBackHrefSource | null | undefined): string {
  const cityAreaHref = srpHrefFromArea(findCityArea(listing?.areas));
  if (cityAreaHref) return cityAreaHref;

  const addressResourceId = cityResourceIdFromAddress(listing?.address);
  return srpHrefFromResourceId(addressResourceId) ?? DEFAULT_HDP_BACK_HREF;
}
